import type { Header, RowData } from '@tanstack/react-table';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronDownIcon,
  ChevronsUpDownIcon,
  EyeOffIcon,
  ListFilterIcon,
  XIcon,
} from 'lucide-react';
import * as React from 'react';

import { useTranslation } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';
import { Popover, PopoverAnchor, PopoverContent } from '@nucleus-commerce/ui/components/popover';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { DataTableFilterPicker } from './data-table-filter-picker';
import type { DataTableFeatures } from './features';
import { isEmptyFilterValue, parseSort, toggleSort, withFilter } from './filter-codec';
import type { DataTableInstance } from './use-data-table';

export interface DataTableColumnHeaderProps<TData extends RowData> {
  table: DataTableInstance<TData>;
  header: Header<DataTableFeatures, TData>;
}

/**
 * A header cell's content: click the label to sort (none, ascending,
 * descending), or open its menu to sort, filter by this column (the same
 * picker and state as the search bar's chips), or hide it.
 */
function DataTableColumnHeader<TData extends RowData>({
  table,
  header,
}: DataTableColumnHeaderProps<TData>) {
  const { t } = useTranslation('common');
  const { column } = header;
  const menuTriggerRef = React.useRef<HTMLButtonElement>(null);
  const [filterOpen, setFilterOpen] = React.useState(false);
  // "Filter" opens the popover after the menu has closed and returned focus,
  // or the focus return would count as an outside click and close it.
  const openFilterAfterMenu = React.useRef(false);

  const label = table.columnLabel(column);
  const align = column.columnDef.meta?.align ?? 'start';
  const sortField = table.sortFieldOf(column);
  const state = parseSort(table.list.sort);
  const sorted =
    sortField !== undefined && state?.field === sortField ? state.direction : undefined;
  const filterDef = table.getFilterDef(column);
  const filterValue = filterDef ? table.list.filters[filterDef.key] : undefined;
  const filtered = !isEmptyFilterValue(filterValue);
  const canHide = column.getCanHide() && column.columnDef.meta?.lockVisibility !== true;
  const hasMenu = sortField !== undefined || filterDef !== undefined || canHide;
  const content = header.isPlaceholder ? null : <table.core.FlexRender header={header} />;
  const SortIcon =
    sorted === 'asc' ? ArrowUpIcon : sorted === 'desc' ? ArrowDownIcon : ChevronsUpDownIcon;
  const setSort = (sort: string | undefined) => table.list.update({ sort });

  const cell = (
    <div
      data-slot="data-table-column-header"
      className={cn(
        'group/head flex min-w-0 items-center gap-0.5',
        align === 'end' && 'flex-row-reverse',
        align === 'center' && 'justify-center',
      )}
    >
      {sortField !== undefined ? (
        <button
          type="button"
          onClick={() => setSort(toggleSort(table.list.sort, sortField))}
          aria-label={t(`indexTable.sort.headerButton.${sorted ?? 'none'}`, { label })}
          className={cn(
            'hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring -mx-1.5 flex h-7 min-w-0 items-center gap-1 rounded-sm px-1.5 outline-none focus-visible:ring-2',
            align === 'end' && 'flex-row-reverse',
          )}
        >
          <span className="truncate">{content}</span>
          <SortIcon
            aria-hidden
            className={cn(
              'size-3.5 shrink-0',
              sorted
                ? 'text-foreground'
                : 'text-muted-foreground opacity-0 group-focus-within/head:opacity-100 group-hover/head:opacity-100',
            )}
          />
        </button>
      ) : (
        <span className="truncate">{content}</span>
      )}
      {filtered ? (
        <ListFilterIcon
          aria-label={t('indexTable.filters.filteredBy', { label })}
          className="text-primary size-3.5 shrink-0"
        />
      ) : null}
      {hasMenu ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              ref={menuTriggerRef}
              variant="ghost"
              size="icon-xs"
              aria-label={t('indexTable.columnOptions', { label })}
              className="text-muted-foreground size-6 shrink-0 opacity-0 group-hover/head:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100 pointer-coarse:opacity-100"
            >
              <ChevronDownIcon aria-hidden />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align={align === 'end' ? 'end' : 'start'}
            className="w-48"
            onCloseAutoFocus={(event) => {
              if (!openFilterAfterMenu.current) return;
              openFilterAfterMenu.current = false;
              event.preventDefault();
              setFilterOpen(true);
            }}
          >
            {sortField !== undefined ? (
              <>
                <DropdownMenuItem onClick={() => setSort(sortField)}>
                  <ArrowUpIcon aria-hidden />
                  {t('indexTable.sort.ascending')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSort(`-${sortField}`)}>
                  <ArrowDownIcon aria-hidden />
                  {t('indexTable.sort.descending')}
                </DropdownMenuItem>
                {sorted ? (
                  <DropdownMenuItem onClick={() => setSort(undefined)}>
                    <XIcon aria-hidden />
                    {t('indexTable.sort.clear')}
                  </DropdownMenuItem>
                ) : null}
              </>
            ) : null}
            {filterDef ? (
              <>
                {sortField !== undefined ? <DropdownMenuSeparator /> : null}
                <DropdownMenuItem
                  onClick={() => {
                    openFilterAfterMenu.current = true;
                  }}
                >
                  <ListFilterIcon aria-hidden />
                  {filtered ? t('indexTable.filters.edit') : t('indexTable.filters.filter')}
                </DropdownMenuItem>
              </>
            ) : null}
            {canHide ? (
              <>
                {sortField !== undefined || filterDef ? <DropdownMenuSeparator /> : null}
                <DropdownMenuItem onClick={() => column.toggleVisibility(false)}>
                  <EyeOffIcon aria-hidden />
                  {t('indexTable.columns.hide')}
                </DropdownMenuItem>
              </>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  );

  if (!filterDef) return cell;
  return (
    <Popover open={filterOpen} onOpenChange={setFilterOpen}>
      <PopoverAnchor asChild>{cell}</PopoverAnchor>
      <PopoverContent
        align="start"
        className="w-auto p-0"
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          menuTriggerRef.current?.focus();
        }}
      >
        <DataTableFilterPicker
          def={filterDef}
          value={filterValue}
          onChange={(value) =>
            table.list.update({
              filters: withFilter(table.list.filters, filterDef.key, value),
            })
          }
          onDone={() => setFilterOpen(false)}
        />
      </PopoverContent>
    </Popover>
  );
}

export { DataTableColumnHeader };
